import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from './firebase';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import './AppleStyle.css';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [invalidCode, setInvalidCode] = useState(false);

    const oobCode = searchParams.get('oobCode');

    // Shared container style for all states
    const containerStyle = {
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '120px',
        paddingBottom: '80px',
        paddingLeft: '15px',
        paddingRight: '15px'
    };

    useEffect(() => {
        if (!oobCode) {
            setInvalidCode(true);
            setIsLoading(false);
            return;
        }

        // Verify the password reset code
        verifyPasswordResetCode(auth, oobCode)
            .then((userEmail) => {
                setEmail(userEmail);
                setIsLoading(false);
            })
            .catch((err) => {
                console.error('Invalid or expired reset code:', err);
                setInvalidCode(true);
                setIsLoading(false);
            });
    }, [oobCode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsSubmitting(true);

        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            setSuccess(true);
        } catch (err) {
            let errorMessage = 'An error occurred';

            switch (err.code) {
                case 'auth/expired-action-code':
                    errorMessage = 'This reset link has expired. Please request a new one.';
                    break;
                case 'auth/invalid-action-code':
                    errorMessage = 'This reset link is invalid. It may have already been used.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak. Please choose a stronger password.';
                    break;
                default:
                    errorMessage = err.message;
            }

            setError(errorMessage);
        }

        setIsSubmitting(false);
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="container" style={containerStyle}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Verifying reset link...</p>
                </div>
            </div>
        );
    }

    // Invalid or expired code
    if (invalidCode) {
        return (
            <div className="container" style={containerStyle}>
                <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
                    <div className="card-body text-center p-4">
                        <div className="text-danger mb-3" role="img" aria-label="Error icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                            </svg>
                        </div>
                        <h4 className="mb-3">Invalid Reset Link</h4>
                        <p className="text-muted mb-4">
                            This password reset link is invalid or has expired. Please request a new password reset.
                        </p>
                        <button
                            className="apple-button"
                            onClick={() => navigate('/')}
                        >
                            Go to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="container" style={containerStyle}>
                <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
                    <div className="card-body text-center p-4">
                        <div className="text-success mb-3" role="img" aria-label="Success icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                            </svg>
                        </div>
                        <h4 className="mb-3">Password Reset Successful</h4>
                        <p className="text-muted mb-4">
                            Your password has been successfully reset. You can now sign in with your new password.
                        </p>
                        <button
                            className="apple-button"
                            onClick={() => navigate('/')}
                        >
                            Go to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Password reset form
    return (
        <div className="container" style={containerStyle}>
            <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
                <div className="card-body p-4">
                    <h4 className="text-center mb-3">Reset Your Password</h4>
                    <p className="text-center text-muted mb-4">
                        Enter a new password for <strong>{email}</strong>
                    </p>

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="newPassword" className="form-label">New Password</label>
                            <input
                                type="password"
                                className="form-control"
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                disabled={isSubmitting}
                                minLength="6"
                                placeholder="Enter new password"
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                            <input
                                type="password"
                                className="form-control"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={isSubmitting}
                                minLength="6"
                                placeholder="Confirm new password"
                            />
                        </div>
                        <button
                            type="submit"
                            className="apple-button w-100"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Resetting Password...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
